import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { identifyUser, resetAnalytics } from '../utilities/analytics';
import { hasPermission } from '../utilities/permissions';
export const useAuth = ({ currentUserQuery, loginQuery, logoutQuery }) => {
    const { loading, data, error, refetch, client } = useQuery(currentUserQuery);
    const navigate = useNavigate();
    const currentUser = useMemo(() => {
        return data?.currentUser;
    }, [data]);
    const [loginMutation] = useMutation(loginQuery);
    const [logoutMutation] = useMutation(logoutQuery);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    useEffect(() => {
        if (currentUser) {
            identifyUser(currentUser);
        }
    }, [currentUser]);
    const login = async (username, password, mfaCode) => {
        try {
            const response = await loginMutation({
                variables: {
                    input: {
                        username,
                        password,
                        mfaCode,
                    },
                },
            });
            await refetch();
            return response;
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error('[Error logging in]', err);
            throw err;
        }
    };
    const logout = async (clientCodes) => {
        setIsLoggingOut(true);
        if (isLoggingOut)
            return;
        try {
            const response = await logoutMutation({
                variables: { clientCodes },
            });
            const usingGuildAuth = response.data?.logout?.usingGuildAuth === true;
            resetAnalytics();
            if (usingGuildAuth) {
                // If using Guild Auth, redirect to federated logout URL.
                // To prevent user from seeing the login page briefly, redirect immediately, don't reset cache.
                // For federated logout flow, it's safe to skip client.cache.reset() before redirect, since the page unloads.
                const logoutUrl = response.data?.logout?.guildAuthLogoutUrl;
                window.location.href = logoutUrl;
                return;
            }
            // For other auth methods, clear Apollo GraphQL cacahe and redirect to home page.
            await client.cache.reset();
            navigate('/');
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error('[Error logging out]', err);
        }
        finally {
            setIsLoggingOut(false);
        }
    };
    return {
        loading,
        error,
        currentUser,
        login,
        logout,
        hasPermission: (...permissions) => hasPermission(currentUser, ...permissions),
    };
};
